export const PARTNER_ONBOARDING_DRAFT_KEY = 'partnerOnboardingDraft';
export const PARTNER_ONBOARDING_DONE_KEY = 'partnerOnboardingDone';
export const PARTNER_ONBOARDING_DEFERRED_KEY = 'partnerOnboardingDeferred';

export type PartnerConsentDocument = 'gdpr' | 'dpa' | 'gcc';
export type PartnerOnboardingStep = 'role' | 'notify' | PartnerConsentDocument | 'connect' | 'activate' | 'done';

export interface PartnerFieldMap {
  flag: string;
  url: string;
  username: string;
  password: string;
  statusToPull: string;
  credentialRef: string;
}

export interface OnboardingPartner {
  id: string;
  name: string;
  logo: string;
  urlPlaceholder: string;
  requiresStatusFilter: boolean;
  fields: PartnerFieldMap;
  serverOnlyFields: string[];
}

export const ONBOARDING_PARTNERS: { [id: string]: OnboardingPartner } = {
  hr4you: {
    id: 'hr4you',
    name: 'HR4YOU',
    logo: 'assets/partners/hr4you.png',
    urlPlaceholder: 'https://kunde.hr4you.de',
    requiresStatusFilter: true,
    fields: {
      flag: 'hr4you',
      url: 'hr4youurl',
      username: 'hr4youusername',
      password: 'hr4youpassword',
      statusToPull: 'hr4youUserStatusToPull',
      credentialRef: 'hr4youCredentialRef'
    },
    serverOnlyFields: ['hr4youTypeOfPullingData']
  }
};

export const PARTNER_SERVER_FIELDS: string[] = Array.from(new Set([
  'partnerOnboarding',
  ...Object.values(ONBOARDING_PARTNERS).reduce((all: string[], partner) =>
    all.concat(Object.values(partner.fields), partner.serverOnlyFields), [])
]));

export interface PartnerOnboardingDraft {
  partner: string;
  step: PartnerOnboardingStep;
}

export interface PartnerStatus {
  partner?: string;
  isOrgAdmin: boolean;
  consents: { [key in PartnerConsentDocument]: boolean };
  connected: boolean;
  activated: boolean;
  searchReady?: boolean;
  connection?: { url: string; username: string; statusToPull: number[] };
}

export interface PartnerConnectForm {
  url: string;
  username: string;
  password: string;
  statusToPull: string;
}

const CONSENT_ORDER: PartnerConsentDocument[] = ['gdpr', 'dpa', 'gcc'];

export function getOnboardingPartner(id: string | null | undefined): OnboardingPartner | null {
  const key = String(id || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(ONBOARDING_PARTNERS, key) ? ONBOARDING_PARTNERS[key] : null;
}

export function partnerFromUrl(search: string): OnboardingPartner | null {
  const query = search.includes('?') ? search.slice(search.indexOf('?') + 1) : search;
  const params = new URLSearchParams(query);
  const explicit = getOnboardingPartner(params.get('partner'));
  if (explicit) return explicit;
  for (const [key, value] of Array.from(params.entries())) {
    const partner = getOnboardingPartner(key) || getOnboardingPartner(value);
    if (partner) return partner;
  }
  return null;
}

export function partnerLegal(partner: OnboardingPartner) {
  return {
    gdpr: {
      title: 'GDPR',
      text: 'Ich akzeptiere die Datenschutzerklärung und willige in die Verarbeitung der Bewerberdaten nach der DSGVO ein.',
      href: 'https://www.mona-ai.de/_files/ugd/b14436_998e9eb20e0541268c178e1a306cea6b.pdf'
    },
    dpa: {
      title: 'DPA',
      text: `Ich akzeptiere den Auftragsverarbeitungsvertrag (DPA) für den Abruf der ${partner.name}-Daten nach MONA.`,
      href: 'https://www.mona-ai.de/businessarea'
    },
    gcc: {
      title: 'GCC',
      text: 'Ich akzeptiere die Allgemeinen Geschäftsbedingungen (GCC).',
      href: 'https://b14436f9-dc40-4f8e-a6ab-199c3202cab6.usrfiles.com/ugd/b14436_c239504ee6de4fd5b9cb898b6e1f5ea0.pdf'
    }
  };
}

export function partnerStorageKey(base: string, partner: OnboardingPartner): string {
  return `${base}:${partner.id}`;
}

export function isValidAdminEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
}

export function buildAdminMailto(partner: OnboardingPartner, adminEmail: string, deepLink: string): string {
  const subject = `MONA in ${partner.name} freischalten`;
  const body = [
    `Bitte öffnen Sie diesen Link als ${partner.name}-Administrator und schließen Sie die Freischaltung von MONA ab:`,
    '',
    deepLink
  ].join('\n');
  return `mailto:${encodeURIComponent(adminEmail.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function adminStep(status: PartnerStatus): PartnerOnboardingStep {
  if (status.activated) return 'done';
  if (!status.isOrgAdmin) return 'notify';
  const open = CONSENT_ORDER.find(doc => !status.consents?.[doc]);
  if (open) return open;
  return status.connected ? 'activate' : 'connect';
}

export function connectFormFromStatus(status: PartnerStatus | null): PartnerConnectForm {
  const connection = status?.connection;
  return {
    url: connection?.url || '',
    username: connection?.username || '',
    password: '',
    statusToPull: (connection?.statusToPull || []).join(', ')
  };
}

export function connectFormError(partner: OnboardingPartner, form: PartnerConnectForm, hasStoredPassword: boolean): string {
  let url: URL;
  try {
    url = new URL(form.url.trim());
  } catch {
    return `Bitte die ${partner.name}-Adresse vollständig angeben, z. B. ${partner.urlPlaceholder}.`;
  }
  if (url.protocol !== 'https:') return `Die ${partner.name}-Adresse muss mit https:// beginnen.`;
  if (!form.username.trim()) return `Bitte den ${partner.name} API-Benutzer angeben.`;
  if (!form.password && !hasStoredPassword) return 'Bitte das Passwort des API-Benutzers angeben.';
  if (partner.requiresStatusFilter) {
    const ids = form.statusToPull.split(/[\s,]+/).filter(Boolean);
    if (!ids.length || ids.some(id => !/^\d{1,9}$/.test(id))) {
      return `Bitte mindestens eine ${partner.name}-Status-ID angeben, z. B. 11 oder 11, 14.`;
    }
  }
  return '';
}

export function partnerErrorMessage(partner: OnboardingPartner | null, err: any): string {
  const name = partner?.name || 'Der Partner';
  const status = err?.status;
  const missing: string[] = err?.error?.missing || [];
  if (status === 403) return 'Nur der Administrator Ihrer Organisation kann diesen Schritt ausführen.';
  if (status === 422) return `${name} hat die Zugangsdaten abgelehnt. Bitte Benutzer und Passwort prüfen.`;
  if (status === 429) return 'Zu viele Fehlversuche. Bitte in 15 Minuten erneut versuchen.';
  if (status === 409 && missing.includes('connection')) return `Bitte zuerst die Verbindung zu ${name} einrichten.`;
  if (status === 409) return 'Bitte zuerst DSGVO, DPA und AGB bestätigen.';
  if (status === 400) return 'Bitte die Eingaben prüfen.';
  return 'Das hat nicht geklappt. Bitte erneut versuchen.';
}
